import type { SeatingTable } from '@/types/seatingPlanner'

export type SeatPosition = {
  /** Index of the seat (0-based) */
  index: number
  /** Chair center x in table-local coordinates */
  x: number
  /** Chair center y in table-local coordinates */
  y: number
  /** Outward unit normal x (-1..1) */
  nx: number
  /** Outward unit normal y (-1..1) */
  ny: number
}

const CHAIR_OFFSET_PX = 12

export function computeSeatPositions(table: SeatingTable): SeatPosition[] {
  const { capacity } = table
  if (capacity <= 0) return []

  if (table.shape === 'round') {
    return computeRoundSeats(table)
  }
  return computeRectangleSeats(table)
}

function computeRoundSeats(table: SeatingTable): SeatPosition[] {
  const cx = table.width / 2
  const cy = table.height / 2
  const radius = Math.min(table.width, table.height) / 2
  const chairR = radius + CHAIR_OFFSET_PX

  return Array.from({ length: table.capacity }, (_, i) => {
    // Start at 12 o'clock, go clockwise.
    const angle = (i / table.capacity) * Math.PI * 2 - Math.PI / 2
    return {
      index: i,
      x: cx + chairR * Math.cos(angle),
      y: cy + chairR * Math.sin(angle),
      nx: Math.cos(angle),
      ny: Math.sin(angle),
    }
  })
}

function computeRectangleSeats(table: SeatingTable): SeatPosition[] {
  const { width, height, capacity } = table
  const perimeter = 2 * (width + height)

  return Array.from({ length: capacity }, (_, i) => {
    const t = ((i + 0.5) / capacity) * perimeter
    return walkPerimeter(t, width, height, i)
  })
}

function walkPerimeter(t: number, width: number, height: number, index: number): SeatPosition {
  // Walk: top (left->right), right (top->bottom), bottom (right->left), left (bottom->top)
  let remaining = t
  if (remaining < width) {
    return { index, x: remaining, y: -CHAIR_OFFSET_PX, nx: 0, ny: -1 }
  }
  remaining -= width
  if (remaining < height) {
    return { index, x: width + CHAIR_OFFSET_PX, y: remaining, nx: 1, ny: 0 }
  }
  remaining -= height
  if (remaining < width) {
    return { index, x: width - remaining, y: height + CHAIR_OFFSET_PX, nx: 0, ny: 1 }
  }
  remaining -= width
  return { index, x: -CHAIR_OFFSET_PX, y: height - remaining, nx: -1, ny: 0 }
}
